import React, { useState, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Table, Icon, Dropdown, Menu, Modal, Button, Radio, Skeleton, Popover, Badge, Tooltip, message, Progress } from "antd";
import moment from "moment";

import * as checklistActions from "~/containers/Checklist/actions";
import * as drawerChecklistActions from "~/containers/DrawerChecklist/actions";

import {
  ConcludedRadioButton,
  LateRadioButton,
  PendingRadioButton,
} from "./styles";
import {
  getAllowedDepartments,
  getPercentageColor,
  getStatusName,
  getStatusStyle,
  getTaskPercentage,
  getTaskTotalPercentage,
  hasOnboarding,
  matchesChecklistSearch,
  CHECKLIST_FILTERS_KEY,
  getDefaultChecklistFilters,
  normalizeChecklistFilters,
  countActiveChecklistFilters,
} from "./helpers";
import { DatePicker } from "~/components/UI/DatePicker/Locale";
import "./styles.css";
import { ModuleHeader } from '~/components/ModuleHeader';
import { getDefaultPaginationConfig } from '~/utils/get-default-pagination-config';
import { STORAGE } from '~/constants/storage.constants';
import { PERMISSIONS } from '~/constants/permissions.constants';
import Search from '~/_shared/components/Search';
import FilterPillsBar from '~/_shared/components/FilterPillsBar';
import RestoredFiltersNotice from '~/_shared/components/RestoredFiltersNotice';
import usePersistedFilters from '~/_shared/hooks/usePersistedFilters';

const { Column } = Table;

const Checklist = () => {
  const dispatch = useDispatch();
  // Filtros mantidos por alguns minutos após o refresh (por escritório/usuário/página).
  const persisted = usePersistedFilters(CHECKLIST_FILTERS_KEY, getDefaultChecklistFilters);
  const applied = normalizeChecklistFilters(persisted.filters);
  const search = applied.search;
  const activeButton = applied.activeButton;
  const setFilters = persisted.setFilters;
  const setSearch = useCallback((value) => setFilters({ search: value }), [setFilters]);
  const setActiveButton = useCallback((value) => setFilters({ activeButton: value }), [setFilters]);
  const resetFilters = persisted.resetFilters;
  const filterCount = countActiveChecklistFilters(applied, persisted.defaults);

  // Selectors for state
  const user = useSelector(state => state.user);
  const permissions = useSelector(state => state.permissions.list);
  const isLoading = useSelector(state => state.checklists.isLoading);
  const allowedDepartments = getAllowedDepartments(user, permissions);
  const checklistsRaw = useSelector(state => state.checklists.list);

  // Format checklists with percentages
  const formattedChecklists = checklistsRaw.map((item) => ({
    ...item,
    legalization_percentage: getTaskPercentage(item, "1", allowedDepartments),
    administrative_percentage: getTaskPercentage(item, "2", allowedDepartments),
    accounting_percentage: getTaskPercentage(item, "3", allowedDepartments),
    fiscal_percentage: getTaskPercentage(item, "4", allowedDepartments),
    personal_percentage: getTaskPercentage(item, "5", allowedDepartments),
    cs_percentage: getTaskPercentage(item, "6", allowedDepartments),
    total_percentage: getTaskTotalPercentage(item, allowedDepartments),
  }));

  const lateChecklists = formattedChecklists.filter((item) => {
    return (
      item.total_percentage < 100 &&
      moment(item.due_at).isBefore(moment(), "months")
    );
  });

  const pendingChecklists = formattedChecklists.filter((item) => {
    return (
      item.total_percentage < 100 &&
      moment(item.due_at).isSameOrAfter(moment(), "months")
    );
  });

  const concludedChecklists = formattedChecklists.filter((item) => {
    return item.total_percentage === 100;
  });

  const checklists = {
    late: lateChecklists,
    pending: pendingChecklists,
    concluded: concludedChecklists,
  };

  // Actions
  const showDrawer = useCallback((data) => dispatch(drawerChecklistActions.showDrawer(data)), [dispatch]);
  const deleteChecklist = useCallback((id) => dispatch(checklistActions.deleteChecklist(id)), [dispatch]);

  // Handlers
  const onAction = (e, item) => {
    switch (e.key) {
      case "edit":
        showDrawer(item);
        break;
      case "delete":
        Modal.confirm({
          title: "Você realmente deseja excluir este checklist?",
          content: "Esta ação não poderá ser desfeita.",
          okText: "Sim",
          okType: "danger",
          cancelText: "Cancelar",
          onOk: () => deleteChecklist(item.id),
        });
        break;
      default:
        return;
    }
  };

  const renderPercentage = (percentage) => (
    percentage === null || percentage === undefined || isNaN(percentage)
      ? <Tooltip title="Nenhuma tarefa deste departamento"><span style={{ color: "#bfbfbf" }}>—</span></Tooltip>
      : <span style={{ color: getPercentageColor(percentage) }}>{percentage}%</span>
  );

  const checkIfHasPermission = (department) => {
    return allowedDepartments.includes(department);
  };

  const hasReviewPermission = () => {
    let userPermissions = [];
    
    if (!user.permissions_group_id) {
      userPermissions = user.permissions;
    } else {
      const permissionsGroup = permissions.find(
        (item) => item.id === user.permissions_group_id
      );
      if (!permissionsGroup) return false;
      userPermissions = permissionsGroup.permissions;
    }

    return userPermissions.includes(PERMISSIONS.checklist.review);
  };

  const [savingOnboardingId, setSavingOnboardingId] = useState(null);

  const saveOnboarding = (record, date) => {
    setSavingOnboardingId(record.id);
    dispatch(checklistActions.updateChecklistOnboarding(record.id, date ? date.format("YYYY-MM-DD") : null))
      .then(() => message.success(date ? "Reunião de onboarding salva." : "Data da reunião removida."))
      .catch(() => message.error("Não foi possível salvar a reunião de onboarding."))
      .finally(() => setSavingOnboardingId(null));
  };

  // Lista vazia diz o porquê e oferece os outros filtros com a contagem, em
  // vez de só "Não há dados" (o padrão é Atrasados, que costuma vir vazio).
  const EMPTY_LABELS = {
    late: { title: "Nenhum checklist atrasado", label: "Atrasados" },
    pending: { title: "Nenhum checklist pendente", label: "Pendentes" },
    concluded: { title: "Nenhum checklist concluído", label: "Concluídos" },
  };

  const renderEmpty = () => {
    if (search) {
      return (
        <div style={{ padding: "24px 0", color: "#8c8c8c" }}>
          <div style={{ fontSize: 14, color: "#595959", marginBottom: 8 }}>Nenhum cliente encontrado para "{search}"</div>
          <Button size="small" onClick={() => setSearch("")}>Limpar busca</Button>
        </div>
      );
    }
    const others = Object.keys(EMPTY_LABELS).filter((key) => key !== activeButton && checklists[key].length > 0);
    return (
      <div style={{ padding: "24px 0", color: "#8c8c8c" }}>
        <div style={{ fontSize: 14, color: "#595959", marginBottom: 8 }}>{EMPTY_LABELS[activeButton].title}</div>
        {others.map((key) => (
          <Button key={key} size="small" style={{ margin: "0 4px" }} onClick={() => setActiveButton(key)}>
            Ver {EMPTY_LABELS[key].label} ({checklists[key].length})
          </Button>
        ))}
      </div>
    );
  };

  const calculateTableWidth = () => {
    let totalWidth = 0;
    
    // Fixed columns that are always visible
    totalWidth += 150; // Status
    totalWidth += 320; // Cliente
    totalWidth += 220; // Reunião de Onboarding
    totalWidth += 120; // Solicitação
    totalWidth += 170; // Competência
    totalWidth += 150; // Vencimento
    totalWidth += 150; // Total
    totalWidth += 100; // Actions
    
    // Conditional columns
    if (hasReviewPermission()) {
      totalWidth += 120; // Revisado
    }
    
    // Department columns
    if (checkIfHasPermission("1")) totalWidth += 150; // Legalização
    if (checkIfHasPermission("2")) totalWidth += 150; // Administrativo
    if (checkIfHasPermission("3")) totalWidth += 150; // Contábil
    if (checkIfHasPermission("4")) totalWidth += 150; // Fiscal
    if (checkIfHasPermission("5")) totalWidth += 150; // Pessoal
    if (checkIfHasPermission("6")) totalWidth += 150; // Sucesso do Cliente
    
    return totalWidth;
  };

  const filteredChecklists = checklists[activeButton]
    .filter((item) => matchesChecklistSearch(item, search))
    .sort((a, b) => moment(b.created_at).diff(moment(a.created_at)));

  return (
    <div>
      <ModuleHeader
          breadcrumbs={['Tarefas', 'Checklist']}
          title="Checklist"
          search={
            <Search
              value={search}
              placeholder="Pesquisar pelo nome ou código do cliente"
              onChange={(e) => setSearch(e.target.value)}
            />
          }
          center={
            <>
              <Popover
                trigger="click"
                placement="bottomRight"
                content={
                  <div style={{ width: 320 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                      <span style={{ fontSize: 15, fontWeight: 600, color: '#262626' }}>Filtros</span>
                      {!persisted.isDefault && (
                        <a
                          style={{ fontSize: 12, color: '#f5222d' }}
                          onClick={resetFilters}
                        >
                          Limpar tudo
                        </a>
                      )}
                    </div>
                    <div>
                      <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Status</div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {[
                          { value: 'late', label: 'Atrasados', color: '#f5222d', count: checklists.late.length },
                          { value: 'pending', label: 'Pendentes', color: '#faad14', count: checklists.pending.length },
                          { value: 'concluded', label: 'Concluídos', color: '#52c41a', count: checklists.concluded.length },
                        ].map(function(opt) {
                          var isActive = activeButton === opt.value;
                          return (
                            <div
                              key={opt.value}
                              onClick={function() { setActiveButton(opt.value); }}
                              style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                padding: '8px 12px', borderRadius: 6, cursor: 'pointer',
                                border: '1.5px solid ' + (isActive ? opt.color : '#e8e8e8'),
                                background: isActive ? opt.color + '0a' : '#fafafa',
                                transition: 'all 0.2s',
                              }}
                            >
                              <div>
                                <div style={{ fontSize: 13, fontWeight: isActive ? 600 : 400, color: isActive ? '#262626' : '#8c8c8c' }}>
                                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: opt.color, display: 'inline-block', marginRight: 8 }} />
                                  {opt.label}
                                </div>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span style={{ fontSize: 13, fontWeight: 600, color: isActive ? opt.color : '#bfbfbf' }}>{opt.count}</span>
                                {isActive && <Icon type="check" style={{ color: opt.color, fontSize: 14 }} />}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                }
              >
                <Badge
                  count={filterCount}
                  offset={[-4, 0]}
                  style={{ backgroundColor: '#722ed1' }}
                >
                  <Button icon="bars" style={{ borderRadius: 8 }}>Filtros</Button>
                </Badge>
              </Popover>
              <Dropdown
                trigger={['click']}
                overlay={
                  <Menu>
                    <Menu.Item key="new" onClick={function() { showDrawer({}); }}>
                      <Icon type="folder-add" /> Novo item
                    </Menu.Item>
                  </Menu>
                }
              >
                <Button icon="plus" shape="circle" />
              </Dropdown>
            </>
          }
          advanced={
            (function() {
              var STATUS_MAP = {
                late: { label: 'Atrasados', color: '#f5222d' },
                pending: { label: 'Pendentes', color: '#faad14' },
                concluded: { label: 'Concluídos', color: '#52c41a' },
              };
              var current = STATUS_MAP[activeButton] || STATUS_MAP.late;
              var pills = [
                { key: 'status', label: current.label + ' (' + checklists[activeButton].length + ')', color: current.color, onRemove: activeButton !== 'late' ? function() { setActiveButton('late'); } : undefined },
              ];
              if (search) {
                pills.push({ key: 'search', label: 'Busca: ' + search, color: '#722ed1', icon: 'search', onRemove: function() { setSearch(''); } });
              }
              return React.createElement(FilterPillsBar, {
                pills: pills,
                onClearAll: !persisted.isDefault ? resetFilters : undefined,
              });
            })()
          }
      />
      <RestoredFiltersNotice
        visible={persisted.wasRestored}
        summary={[
          activeButton !== 'late' ? ({ pending: 'Pendentes', concluded: 'Concluídos' })[activeButton] : null,
          search ? 'Busca: ' + search : null,
        ].filter(Boolean).join(' · ')}
        onReset={resetFilters}
        onDismiss={persisted.dismissRestored}
      />
      <div className="table-clabs checklist-table">
        {isLoading ? (
          <div style={{ padding: 24 }}>
            {[...Array(8)].map((_, idx) => (
              <Skeleton key={idx} active paragraph={{ rows: 1, width: '100%' }} title={false} style={{ marginBottom: 16 }} />
            ))}
          </div>
        ) : (
          <Table
            rowKey="id"
            loading={isLoading}
            dataSource={filteredChecklists}
            pagination={getDefaultPaginationConfig({
              storageKey: STORAGE.defaultPageSize.checklists,
              defaultPageSize: 1000,
            })}
            bordered={true}
            rowClassName={(record) => (hasReviewPermission() && record.is_revised) ? "row-clabs row-revised" : "row-clabs"}
            locale={{ emptyText: renderEmpty() }}
            scroll={{ x: calculateTableWidth(), y: `${window.innerHeight - 360}px` }}
          >
            <Column
              title="Status"
              dataIndex="status"
              key="status"
              align="center"
              width={150}
              fixed="left"
              ellipsis
              render={(status) => (
                <Button value={1} size="small" style={getStatusStyle(status)}>
                  {getStatusName(status)}
                </Button>
              )}
              filters={[
                { text: getStatusName(3), value: 3 },
                { text: getStatusName(4), value: 4 },
                { text: getStatusName(2), value: 2 },
                { text: getStatusName(1), value: 1 },
              ]}
              filterMultiple={false}
              onFilter={(value, record) => record.status === value}
            />
            <Column
              title="Cliente"
              key="customer_name"
              dataIndex="customer.name"
              fixed="left"
              width={320}
              ellipsis
              render={(name, record) => {
                if (!record.customer) return null;
                const fullName = record.customer.cod
                  ? `${record.customer.cod} - ${name}`
                  : name;
                // O nome inteiro fica no hover; a célula corta com reticências.
                return (
                  <Tooltip title={fullName} placement="topLeft">
                    <div style={{ maxWidth: 288, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {fullName}
                    </div>
                  </Tooltip>
                );
              }}
            />
            <Column
              title="Total"
              dataIndex="total_percentage"
              key="total_percentage"
              width={150}
              render={(percentage) => (
                percentage === null || percentage === undefined || isNaN(percentage)
                  ? renderPercentage(null)
                  : <Progress
                      percent={percentage}
                      size="small"
                      strokeColor={getPercentageColor(percentage)}
                      format={(value) => <span style={{ color: getPercentageColor(value) }}>{value}%</span>}
                    />
              )}
            />
            <Column
              title={<span style={{ whiteSpace: "nowrap" }}>Reunião de Onboarding</span>}
              dataIndex="onboarding_meeting_at"
              key="onboarding_meeting_at"
              align="center"
              width={220}
              render={(date, record) => {
                // Saída e encerramento não têm onboarding: campo cinza, travado.
                if (!hasOnboarding(record.status)) {
                  return (
                    <Tooltip title="Sem onboarding: o cliente está saindo">
                      <div style={{ background: "#f0f0f0", border: "1px solid #e8e8e8", borderRadius: 4, height: 24, cursor: "not-allowed" }} />
                    </Tooltip>
                  );
                }

                return (
                  <DatePicker
                    size="small"
                    value={date ? moment(date, "YYYY-MM-DD") : null}
                    placeholder="Definir data"
                    disabled={savingOnboardingId === record.id}
                    onChange={(value) => saveOnboarding(record, value)}
                    getPopupContainer={() => document.body}
                  />
                );
              }}
            />
            {hasReviewPermission() && (
              <Column
                title="Revisado"
                dataIndex="is_revised"
                key="is_revised"
                width={120}
                ellipsis
                filters={[
                  { text: "Sim", value: true },
                  { text: "Não", value: false },
                ]}
                filterMultiple={false}
                onFilter={(value, record) => {
                  if (value === true) {
                    return record.is_revised === true;
                  } else {
                    return record.is_revised !== true;
                  }
                }}
                render={(val) => (val ? "Sim" : "Não")}
              />
            )}
            <Column
              title="Solicitação"
              dataIndex="requested_at"
              key="requested_at"
              align="center"
              width={120}
              ellipsis
              render={(date) => moment(date).format("DD/MM/YYYY")}
            />
            <Column
              title="Competência"
              dataIndex="competency_at"
              key="competency_at"
              align="center"
              width={170}
              ellipsis
              render={(date, record) =>
                date
                  ? `${[1, 3].includes(record.status) ? "até" : "a partir de"} ${moment(date).format("MM/YYYY")}`
                  : ""
              }
            />
            <Column
              title="Vencimento"
              dataIndex="due_at"
              key="due_at"
              align="center"
              width={150}
              ellipsis
              render={(date) => (date ? moment(date).format("MM/YYYY") : "")}
            />
            {checkIfHasPermission("1") && (
              <Column
                title="Legalização"
                align="center"
                dataIndex="legalization_percentage"
                width={150}
                ellipsis
                render={(percentage) => renderPercentage(percentage)}
              />
            )}
            {checkIfHasPermission("2") && (
              <Column
                title="Administrativo"
                align="center"
                dataIndex="administrative_percentage"
                width={150}
                ellipsis
                render={(percentage) => renderPercentage(percentage)}
              />
            )}
            {checkIfHasPermission("3") && (
              <Column
                title="Contábil"
                align="center"
                dataIndex="accounting_percentage"
                width={150}
                ellipsis
                render={(percentage) => renderPercentage(percentage)}
              />
            )}
            {checkIfHasPermission("4") && (
              <Column
                title="Fiscal"
                align="center"
                dataIndex="fiscal_percentage"
                width={150}
                ellipsis
                render={(percentage) => renderPercentage(percentage)}
              />
            )}
            {checkIfHasPermission("5") && (
              <Column
                title="Pessoal"
                align="center"
                dataIndex="personal_percentage"
                width={150}
                ellipsis
                render={(percentage) => renderPercentage(percentage)}
              />
            )}
            {checkIfHasPermission("6") && (
              <Column
                title="Sucesso do Cliente"
                align="center"
                dataIndex="cs_percentage"
                width={150}
                ellipsis
                render={(percentage) => renderPercentage(percentage)}
              />
            )}
            <Column
              dataIndex="actions"
              key="actions"
              width={100}
              fixed="right"
              ellipsis
              render={(text, record, index) => (
                <center key={index}>
                  <Dropdown
                    overlay={
                      <Menu onClick={(e) => onAction(e, record)}>
                        <Menu.Item key="edit">
                          <Icon type="edit" /> Editar
                        </Menu.Item>
                        <Menu.Divider />
                        <Menu.Item key="delete">
                          <Icon type="delete" /> Excluir
                        </Menu.Item>
                      </Menu>
                    }
                    trigger={["click"]}
                  >
                    <a className="ant-dropdown-link" href="#">
                      Ações <Icon type="down" />
                    </a>
                  </Dropdown>
                </center>
              )}
            />
          </Table>
        )}
      </div>
    </div>
  );
};

export default Checklist;
